import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    CacheInfo,
    evictPolarisCache,
    fetchPolarisCaches,
} from "../services/polarisService";
import "../styles/polaris-admin.css";
import PolarisConfirmModal from "./PolarisConfirmModal";

function formatDate(value?: string | null): string {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleString();
    } catch {
        return value;
    }
}

function groupByTier(caches: CacheInfo[]): { tier: string; caches: CacheInfo[] }[] {
    const order: string[] = [];
    const map = new Map<string, CacheInfo[]>();
    for (const cache of caches) {
        if (!map.has(cache.tier)) {
            order.push(cache.tier);
            map.set(cache.tier, []);
        }
        map.get(cache.tier)!.push(cache);
    }
    return order.map((tier) => ({ tier, caches: map.get(tier)! }));
}

export default function PolarisCacheManagementPage() {
    const [caches, setCaches] = useState<CacheInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [pendingCache, setPendingCache] = useState<CacheInfo | null>(null);
    const [evicting, setEvicting] = useState(false);
    const [modalError, setModalError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setError(null);
        try {
            const data = await fetchPolarisCaches();
            setCaches(data);
        } catch (e: any) {
            setError(e?.message ?? "Failed to load caches.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const groups = useMemo(() => groupByTier(caches), [caches]);

    const openConfirm = (cache: CacheInfo) => {
        setNotice(null);
        setModalError(null);
        setPendingCache(cache);
    };

    const closeConfirm = () => {
        if (evicting) return;
        setPendingCache(null);
        setModalError(null);
    };

    const handleConfirmEvict = async () => {
        if (!pendingCache) return;
        setEvicting(true);
        setModalError(null);
        try {
            const result = await evictPolarisCache(pendingCache.name);
            setNotice(
                `Busted cache “${result.cacheName}” at ${formatDate(result.evictedAt)}. The next request will reload from the source.`
            );
            setPendingCache(null);
        } catch (e: any) {
            setModalError(e?.message ?? "Failed to bust cache.");
        } finally {
            setEvicting(false);
        }
    };

    return (
        <section className="pa-section">
            <h1 className="pa-page-title">Cache Management</h1>

            <div className="pa-metric-card pa-metric-card-full" style={{ marginBottom: 24 }}>
                <div className="pa-metric-card-title">About these caches</div>
                <div className="pa-chart-empty" style={{ textAlign: "left" }}>
                    Bust a cache to force the next request to reload fresh CMS or reference data
                    instead of waiting for the automatic TTL. Caches are in-memory on each server
                    instance — in multi-instance environments this clears only the instance that
                    handles the request.
                </div>
            </div>

            {error && <div className="pa-error">{error}</div>}
            {notice && (
                <div className="pa-success" role="status">
                    {notice}
                </div>
            )}
            {loading && <div className="pa-loading">Loading caches…</div>}

            {!loading &&
                groups.map(({ tier, caches: tierCaches }) => (
                    <div key={tier} style={{ marginBottom: 32 }}>
                        <h2 className="pa-section-title">{tier}</h2>
                        <div className="pa-table-wrapper">
                            <table className="pa-table">
                                <thead>
                                    <tr>
                                        <th>Cache</th>
                                        <th>TTL</th>
                                        <th>Description</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tierCaches.map((cache) => (
                                        <tr key={cache.name}>
                                            <td>
                                                <code>{cache.name}</code>
                                            </td>
                                            <td>{cache.ttlDescription}</td>
                                            <td>{cache.description}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="pa-page-btn"
                                                    onClick={() => openConfirm(cache)}
                                                    title={`Bust cache ${cache.name}`}
                                                >
                                                    Bust cache
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ))}

            <PolarisConfirmModal
                open={pendingCache != null}
                title="Bust cache?"
                message={
                    pendingCache ? (
                        <>
                            Bust <strong>{pendingCache.name}</strong>? The next request will reload
                            this data from its source.
                        </>
                    ) : null
                }
                confirmLabel="Bust cache"
                loadingLabel="Busting…"
                loading={evicting}
                error={modalError}
                onConfirm={handleConfirmEvict}
                onClose={closeConfirm}
            />
        </section>
    );
}
